import { noop } from '@crocodille/common';
import Deferred from '@components/deferred/deferred';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import CartComponent from '@components/cart/cart/cart';
import AppLayoutHeaderLogo from './app-layout-header-logo';
import styles from './app-layout.module.scss';
import RouteChangeIndicator from './route-change-indicator/route-change-indicator';
import * as routing from '../../routing';
import MainNavigation from '@sections/main-navigation/main-navigation';
import AppFooter from '@sections/footer-2/footer-2';
import LocaleSwitcher from '@components/locale-switcher/locale-switcher';
import HamburgerButton from '@components/hamburger-button/hamburger-button';
import { Alert } from '@components/alert/alert';

const AppLayoutCart = dynamic(() => import('./app-layout-cart'), {
  ssr: false,
});

const ShippingStatus = dynamic(
  () => import('@components/shipping/shipping-status/shipping-status'),
  { ssr: false },
);

const AppLayoutNavigation = dynamic(() => import('./app-layout-navigation'), {
  ssr: false,
});

const CartStatus = dynamic(
  () => import('@components/cart/cart-status/cart-status'),
  {
    ssr: false,
  },
);

const AuthStatus = dynamic(
  () => import('@components/auth/auth-status/auth-status'),
  { ssr: false },
);

const CartFloatingButton = dynamic(
  () => import('@components/cart/cart-floating-button/cart-floating-button'),
  { ssr: false },
);

const LoyaltyStatus = dynamic(
  () => import('@components/loyalty/loyalty-status/loyalty-status'),
  { ssr: false },
);

export function useAppLayoutState() {
  return useContext(AppLayoutContext);
}

/**
 * Floating header.
 */
const Header: React.FC = () => {
  const {
    headless,
    navigationOpen,
    toggleNavigationOpen,
    cartOpen,
    toggleCartOpen,
    cartEnabled,
  } = useAppLayoutState();

  const { route } = useRouter();

  if (headless) {
    return null;
  }

  return (
    <header
      className={cn(styles.header, {
        [styles.isNavigationOpen]: navigationOpen,
        [styles.isCartOpen]: cartOpen,
        [styles.isShadowHidden]:
          routing.isMenuRoute(route) || routing.isProductsRoute(route),
      })}
    >
      <div className={styles.headerInner}>
        <RouteChangeIndicator />
        <AppLayoutHeaderLogo />
        <ShippingStatus />
        <Spacer />
        <div className={styles.loyaltyStatusContainer}>
          <LoyaltyStatus />
        </div>
        <Spacer />
        <LocaleSwitcher />
        <AuthStatus />
        {cartEnabled ? (
          <CartStatus open={cartOpen} onPress={toggleCartOpen} />
        ) : null}
        <HamburgerButton
          toggled={navigationOpen}
          onToggle={() => toggleNavigationOpen()}
        />
      </div>
    </header>
  );
};

/**
 * Fill available space within flex container.
 */
const Spacer: React.FC = () => {
  return <div className={styles.spacer} />;
};

interface AppLayoutContextValue {
  /**
   * Render without header and footer.
   */
  readonly headless: boolean;
  /**
   * Stores whether the navigation is open or not unless it's forced open.
   */
  readonly navigationOpen: boolean;
  /**
   * Toggles the navigation open state or sets it to the provided state.
   *
   * @param state The state of the menu to set
   */
  toggleNavigationOpen(state?: boolean): void;
  /**
   * Enabled cart
   */
  readonly cartEnabled: boolean;
  /**
   * Cart sidebar state. Controls whether the car is shown or not unless it's forced shown.
   */
  readonly cartOpen: boolean;
  /**
   * Toggle or set the state of the cart sidebar.
   *
   * @param state State to put the cart sidebar to.
   */
  toggleCartOpen(state?: boolean): void;
}

const AppLayoutContext = createContext<AppLayoutContextValue>({
  headless: false,
  navigationOpen: false,
  toggleNavigationOpen: noop,
  cartOpen: false,
  cartEnabled: true,
  toggleCartOpen: noop,
});

/**
 * Renders menu according to screen size.
 */
const Navigation: React.FC<{ children: () => React.JSX.Element }> = ({
  children,
}) => {
  const { navigationOpen: open, toggleNavigationOpen: toggleOpen } =
    useAppLayoutState();

  //
  // When navigation happens, the menu should be closed.
  //
  const { events } = useRouter();
  useEffect(() => {
    const handler = (_route: string, options: { shallow?: boolean } = {}) => {
      const { shallow = false } = options;
      if (!shallow) {
        toggleOpen(false);
      }
    };

    events.on('routeChangeComplete', handler);

    return () => events.off('routeChangeComplete', handler);
  }, [events, toggleOpen]);

  return (
    <AppLayoutNavigation open={open}>
      <Deferred shown={open}>{children}</Deferred>
    </AppLayoutNavigation>
  );
};

const Cart: React.FC<{ children: () => React.JSX.Element }> = ({
  children,
}) => {
  const { headless, cartOpen, cartEnabled, toggleCartOpen } =
    useAppLayoutState();

  if (headless) {
    return null;
  }

  return (
    <AppLayoutCart open={cartOpen && cartEnabled} toggleOpen={toggleCartOpen}>
      <Deferred shown={cartOpen && cartEnabled}>{children}</Deferred>
    </AppLayoutCart>
  );
};

/**
 * Main page content
 */
const Content: React.FC<React.PropsWithChildren<Record<string, unknown>>> = ({
  children,
}) => {
  return <main className={styles.content}>{children}</main>;
};

export interface LayoutProps {
  readonly cartEnabled?: boolean;
  readonly headless?: boolean;
}

/**
 * Dictates the high level application layout.
 */
const AppLayoutElement: React.FC<React.PropsWithChildren<LayoutProps>> = ({
  children,
  cartEnabled = false,
  headless = false,
}) => {
  const router = useRouter();

  const [mainMenuOpen, setMainMenuOpen] = useState(false);

  const toggleMainMenuOpen = useCallback(
    (forceState?: boolean) =>
      typeof forceState === 'boolean'
        ? setMainMenuOpen(forceState)
        : setMainMenuOpen((open) => !open),
    [setMainMenuOpen],
  );

  const [cartOpen, setCartOpen] = useState(false);
  const toggleCartOpen = useCallback(
    (forceState?: boolean) =>
      typeof forceState === 'boolean'
        ? setCartOpen(forceState)
        : setCartOpen((open) => !open),
    [setCartOpen],
  );

  useEffect(() => {
    const onRouteChange = (
      _route: string,
      options: { shallow?: boolean } = {},
    ) => {
      const { shallow = false } = options;
      if (!shallow) {
        setCartOpen(false);
      }
    };

    router.events.on('routeChangeStart', onRouteChange);

    return () => {
      router.events.off('routeChangeStart', onRouteChange);
    };
  }, [router.events]);

  const layoutContext = useMemo(
    (): AppLayoutContextValue => ({
      headless,
      navigationOpen: mainMenuOpen,
      toggleNavigationOpen: toggleMainMenuOpen,
      cartOpen: cartEnabled ? cartOpen : false,
      cartEnabled,
      toggleCartOpen,
    }),
    [
      headless,
      cartEnabled,
      cartOpen,
      mainMenuOpen,
      toggleCartOpen,
      toggleMainMenuOpen,
    ],
  );

  return (
    <AppLayoutContext.Provider value={layoutContext}>
      <div
        suppressHydrationWarning
        className={cn(styles.container, {
          [styles.isHeadless]: headless,
        })}
      >
        <Header />

        {children}

        {cartEnabled ? <CartFloatingButton onToggle={toggleCartOpen} /> : null}
      </div>
      <Alert />
    </AppLayoutContext.Provider>
  );
};

const AppLayout = Object.assign(AppLayoutElement, {
  Content,
  Navigation,
  Cart,
});

export function getAppLayoutWithoutCart(children: React.ReactNode) {
  return (
    <AppLayout headless={false} cartEnabled={false}>
      <AppLayout.Navigation>{() => <MainNavigation />}</AppLayout.Navigation>

      <AppLayout.Content>{children}</AppLayout.Content>

      <AppFooter />
    </AppLayout>
  );
}

export function getAppLayoutWithCart(children: React.ReactNode) {
  return (
    <AppLayout headless={false} cartEnabled={true}>
      <AppLayout.Navigation>{() => <MainNavigation />}</AppLayout.Navigation>

      <AppLayout.Content>{children}</AppLayout.Content>

      <AppLayout.Cart>{() => <CartComponent />}</AppLayout.Cart>

      <AppFooter />
    </AppLayout>
  );
}

export default AppLayout;
